import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getCachedSignedUrl } from '../utils/storage';

const getLevelInfo = (scoreValue) => {
  const thresholds = [0, 100, 300, 600, 1000];
  let level = 1;
  let current = 0;
  let next = 100;

  for (let i = thresholds.length - 1; i >= 0; i -= 1) {
    if (scoreValue >= thresholds[i]) {
      level = i + 1;
      current = thresholds[i];
      next = thresholds[i + 1] ?? (thresholds[i] + 500);
      break;
    }
  }

  const progress = next > current
    ? Math.min(100, ((scoreValue - current) / (next - current)) * 100)
    : 100;

  return { level, current, next, progress };
};

const getYoutubeVideoId = (rawUrl) => {
  const url = String(rawUrl || '').trim();
  if (!url) return '';
  const directId = url.match(/^[a-zA-Z0-9_-]{11}$/)?.[0];
  if (directId) return directId;
  try {
    const u = new URL(url);
    const host = (u.hostname || '').replace(/^www\./, '');
    if (host === 'youtu.be') return (u.pathname || '').replace('/', '').slice(0, 11);
    const v = u.searchParams.get('v');
    if (v) return v.slice(0, 11);
    const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
    if (m?.[1]) return String(m[1]).slice(0, 11);
    return '';
  } catch {
    return '';
  }
};

const getYoutubeThumb = (rawUrl) => {
  const id = getYoutubeVideoId(rawUrl);
  if (!id) return '';
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
};

const htmlToText = (html) => String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const mapNewUsersWithStats = async (userRows) => {
  const baseUsers = (userRows || []).map((u) => ({
    id: u.id,
    username: u.username,
    avatarUrl: u.avatar_url || '',
    score: Number(u.score || 0),
  }));

  const userIds = baseUsers.map((u) => u.id).filter(Boolean);

  const [projectsRes, challengesRes, proofsRes] = userIds.length > 0
    ? await Promise.all([
      supabase
        .from('ch_projects')
        .select('id, owner_id')
        .in('owner_id', userIds)
        .eq('is_public', true),
      supabase
        .from('ch_challenges')
        .select('id, owner_id, goal_type, target_count, total_goals')
        .in('owner_id', userIds)
        .eq('is_public', true),
      supabase
        .from('ch_proofs')
        .select('id, user_id, challenge_id, submitted_at')
        .in('user_id', userIds)
        .eq('is_public', true)
        .order('submitted_at', { ascending: false })
        .limit(2000),
    ])
    : [{ data: [] }, { data: [] }, { data: [] }];

  const projectsByUser = new Map();
  (projectsRes?.data || []).forEach((p) => {
    if (!p?.owner_id) return;
    projectsByUser.set(p.owner_id, (projectsByUser.get(p.owner_id) || 0) + 1);
  });

  const proofsByUser = new Map();
  const attendanceDaySetByUser = new Map();
  const proofCountByChallengeId = new Map();

  (proofsRes?.data || []).forEach((pr) => {
    if (pr?.user_id) {
      proofsByUser.set(pr.user_id, (proofsByUser.get(pr.user_id) || 0) + 1);
      const dayKey = pr.submitted_at ? String(pr.submitted_at).slice(0, 10) : '';
      if (dayKey) {
        const set = attendanceDaySetByUser.get(pr.user_id) || new Set();
        set.add(dayKey);
        attendanceDaySetByUser.set(pr.user_id, set);
      }
    }
    if (pr?.challenge_id) {
      proofCountByChallengeId.set(pr.challenge_id, (proofCountByChallengeId.get(pr.challenge_id) || 0) + 1);
    }
  });

  const completedChallengesByUser = new Map();
  (challengesRes?.data || []).forEach((ch) => {
    const ownerId = ch?.owner_id;
    if (!ownerId || !ch?.id) return;

    const goalType = ch.goal_type === 'thirty-day' ? 'thirty_day' : ch.goal_type;
    const total = goalType === 'thirty_day'
      ? 30
      : Number(ch.total_goals ?? ch.target_count ?? 0);
    const done = proofCountByChallengeId.get(ch.id) || 0;
    if (total > 0 && done >= total) {
      completedChallengesByUser.set(ownerId, (completedChallengesByUser.get(ownerId) || 0) + 1);
    }
  });

  return baseUsers.map((u) => {
    const levelInfo = getLevelInfo(u.score);
    const attendanceDays = attendanceDaySetByUser.get(u.id)?.size || 0;
    return {
      id: u.id,
      username: u.username,
      avatarUrl: u.avatarUrl,
      level: levelInfo.level,
      levelProgress: levelInfo.progress,
      attendanceDays,
      totalProjects: projectsByUser.get(u.id) || 0,
      completedChallenges: completedChallengesByUser.get(u.id) || 0,
      submittedProofs: proofsByUser.get(u.id) || 0,
    };
  });
};

export const useLandingData = () => {
  const [publicProjects, setPublicProjects] = useState([]);
  const [latestChallenges, setLatestChallenges] = useState([]);
  const [recentPublicProofs, setRecentPublicProofs] = useState([]);
  const [recentFeedPosts, setRecentFeedPosts] = useState([]);
  const [recentNotices, setRecentNotices] = useState([]);
  const [recentLabs, setRecentLabs] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [monthlyRankings, setMonthlyRankings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: noticeRows } = await supabase
        .from('ch_notices')
        .select('id, title, author_name, created_at, files:ch_notice_files(file_path, content_type)')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(1);

      setRecentNotices(
        (noticeRows || []).map((n) => {
          const img = (n.files || []).find((f) => String(f?.content_type || '').startsWith('image/') && f?.file_path);
          const thumbnailUrl = img?.file_path
            ? supabase.storage.from('notice-files').getPublicUrl(img.file_path).data.publicUrl
            : '';
          return {
            id: n.id,
            title: n.title,
            authorName: n.author_name || '꿈현공장장',
            date: n.created_at ? String(n.created_at).slice(0, 10) : '',
            thumbnailUrl,
          };
        })
      );

      const { data: labsRows } = await supabase
        .from('ch_labs_posts')
        .select('id, title, author_name, created_at, replies:ch_labs_post_replies(id)')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(1);

      setRecentLabs(
        (labsRows || []).map((x) => ({
          id: x.id,
          title: x.title,
          authorName: x.author_name || '익명',
          date: x.created_at ? String(x.created_at).slice(0, 10) : '',
          hasReply: Array.isArray(x.replies) && x.replies.length > 0,
        }))
      );

      const { data: feedRows } = await supabase
        .from('ch_feed_posts')
        .select('id, title, author_name, content_html, created_at, thumbnail_image_path, view_count')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(8);

      const feedIds = (feedRows || []).map((x) => x.id).filter(Boolean);
      const { data: feedImageRows } = feedIds.length > 0
        ? await supabase
          .from('ch_feed_images')
          .select('post_id, file_path, created_at')
          .in('post_id', feedIds)
          .order('created_at', { ascending: true })
        : { data: [] };

      const firstImageByPostId = (feedImageRows || []).reduce((acc, row) => {
        if (!row?.post_id || acc.has(row.post_id)) return acc;
        acc.set(row.post_id, row.file_path || '');
        return acc;
      }, new Map());

      const [feedLikeRowsRes, feedCommentRowsRes] = await Promise.all([
        feedIds.length > 0
          ? supabase
            .from('ch_feed_post_likes')
            .select('post_id')
            .in('post_id', feedIds)
          : Promise.resolve({ data: [] }),
        feedIds.length > 0
          ? supabase
            .from('ch_feed_comments')
            .select('post_id')
            .in('post_id', feedIds)
          : Promise.resolve({ data: [] }),
      ]);

      const likeCountByPostId = (feedLikeRowsRes?.data || []).reduce((acc, row) => {
        if (!row?.post_id) return acc;
        acc.set(row.post_id, (acc.get(row.post_id) || 0) + 1);
        return acc;
      }, new Map());

      const commentCountByPostId = (feedCommentRowsRes?.data || []).reduce((acc, row) => {
        if (!row?.post_id) return acc;
        acc.set(row.post_id, (acc.get(row.post_id) || 0) + 1);
        return acc;
      }, new Map());

      setRecentFeedPosts(
        (feedRows || []).map((row) => {
          const thumbPath = row.thumbnail_image_path || firstImageByPostId.get(row.id) || '';
          const thumbnailUrl = thumbPath
            ? supabase.storage.from('feed-images').getPublicUrl(thumbPath).data.publicUrl
            : '';

          return {
            id: row.id,
            title: row.title || '제목 없음',
            authorName: row.author_name || '익명',
            createdAt: row.created_at ? String(row.created_at).slice(0, 10) : '',
            excerpt: htmlToText(row.content_html).slice(0, 90),
            thumbnailUrl,
            likeCount: Number(likeCountByPostId.get(row.id) || 0),
            commentCount: Number(commentCountByPostId.get(row.id) || 0),
            viewCount: Number(row.view_count || 0),
          };
        })
      );

      const { data: userRows } = await supabase
        .from('ch_profiles')
        .select('id, username, avatar_url, created_at, score')
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(12);

      setNewUsers(await mapNewUsersWithStats(userRows));

      const { data: projectRows } = await supabase.rpc('ch_list_public_projects_with_stats', {
        p_limit: 8,
        p_offset: 0,
      });

      const mappedProjects = await Promise.all(
        (projectRows || []).map(async (p) => {
          const coverUrl = p?.cover_image_path
            ? await getCachedSignedUrl({ bucket: 'project-covers', path: p.cover_image_path })
            : '';

          return {
            id: p.id,
            title: p.title,
            description: p.description,
            coverImage: coverUrl,
            coverImageAlt: p.cover_image_alt || p.title,
            totalChallenges: p.total_challenges ?? 0,
            completedProofs: p.completed_proofs ?? 0,
            totalLikes: p.total_likes ?? 0,
            averageStars: Number(p.average_stars ?? 0),
            isArchived: p.is_archived ?? false,
            createdAt: p.created_at,
            status: p.status || 'active',
          };
        })
      );

      setPublicProjects(mappedProjects);

      const now = new Date();
      const { data: rankingRows } = await supabase.rpc('ch_get_monthly_proof_rankings', {
        p_year: now.getUTCFullYear(),
        p_month: now.getUTCMonth() + 1,
        p_limit: 5,
      });
      setMonthlyRankings(rankingRows || []);

      const { data: challengeRows } = await supabase
        .from('ch_challenges')
        .select('id, owner_id, title, description, goal_type, target_count, total_goals, cover_image_path, cover_image_alt, created_at, project:ch_projects(title)')
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(30);

      const { data: recentProofForSort } = await supabase
        .from('ch_proofs')
        .select('challenge_id, submitted_at')
        .eq('is_public', true)
        .order('submitted_at', { ascending: false })
        .limit(50);

      const latestProofAtByChallengeId = new Map();
      (recentProofForSort || []).forEach((r) => {
        if (!r?.challenge_id || !r?.submitted_at) return;
        if (!latestProofAtByChallengeId.has(r.challenge_id)) {
          latestProofAtByChallengeId.set(r.challenge_id, r.submitted_at);
        }
      });

      const sortedChallenges = (challengeRows || [])
        .slice()
        .sort((a, b) => {
          const aKey = latestProofAtByChallengeId.get(a.id) || a.created_at;
          const bKey = latestProofAtByChallengeId.get(b.id) || b.created_at;
          return new Date(bKey) - new Date(aKey);
        })
        .slice(0, 8);

      const selectedChallengeIds = sortedChallenges.map((c) => c.id).filter(Boolean);
      const ownerByChallengeId = new Map((sortedChallenges || []).map((c) => [c.id, c.owner_id]));

      const { data: selectedProofRows } = selectedChallengeIds.length > 0
        ? await supabase
          .from('ch_proofs')
          .select('id, challenge_id, user_id, rating')
          .in('challenge_id', selectedChallengeIds)
          .eq('is_public', true)
        : { data: [] };

      const proofRowsForOwner = (selectedProofRows || []).filter((p) => {
        const ownerId = ownerByChallengeId.get(p.challenge_id);
        if (!ownerId) return false;
        return p.user_id === ownerId;
      });

      const completedByChallengeId = new Map();
      const ratingSumByChallengeId = new Map();
      const ratingCountByChallengeId = new Map();
      const proofIds = [];

      proofRowsForOwner.forEach((p) => {
        completedByChallengeId.set(p.challenge_id, (completedByChallengeId.get(p.challenge_id) || 0) + 1);
        proofIds.push(p.id);
        const rating = Number(p.rating || 0);
        if (Number.isFinite(rating) && rating > 0) {
          ratingSumByChallengeId.set(p.challenge_id, (ratingSumByChallengeId.get(p.challenge_id) || 0) + rating);
          ratingCountByChallengeId.set(p.challenge_id, (ratingCountByChallengeId.get(p.challenge_id) || 0) + 1);
        }
      });

      const { data: likeRows } = proofIds.length > 0
        ? await supabase
          .from('ch_proof_likes')
          .select('proof_id')
          .in('proof_id', proofIds)
        : { data: [] };

      const likeCountByProofId = new Map();
      (likeRows || []).forEach((lk) => {
        likeCountByProofId.set(lk.proof_id, (likeCountByProofId.get(lk.proof_id) || 0) + 1);
      });

      const likesByChallengeId = new Map();
      proofRowsForOwner.forEach((p) => {
        likesByChallengeId.set(
          p.challenge_id,
          (likesByChallengeId.get(p.challenge_id) || 0) + (likeCountByProofId.get(p.id) || 0)
        );
      });

      const mappedChallenges = await Promise.all(
        sortedChallenges.map(async (c) => {
          const coverUrl = c?.cover_image_path
            ? await getCachedSignedUrl({ bucket: 'challenge-covers', path: c.cover_image_path })
            : '';
          const goalType = c.goal_type === 'thirty-day' ? 'thirty_day' : c.goal_type;
          const totalGoals = goalType === 'thirty_day'
            ? 30
            : Number(c.total_goals ?? c.target_count ?? 0);

          const completedProofs = completedByChallengeId.get(c.id) || 0;
          const ratingSum = ratingSumByChallengeId.get(c.id) || 0;
          const ratingCount = ratingCountByChallengeId.get(c.id) || 0;
          const averageStars = ratingCount > 0 ? ratingSum / ratingCount : 0;
          const totalLikes = likesByChallengeId.get(c.id) || 0;

          return {
            id: c.id,
            title: c.title,
            description: c.description || '',
            coverImage: coverUrl,
            coverImageAlt: c.cover_image_alt || c.title,
            goalType,
            totalGoals,
            completedProofs,
            totalLikes,
            averageStars: Number(averageStars || 0),
            estimatedTime: '',
            isCompleted: totalGoals > 0 ? completedProofs >= totalGoals : false,
            isLocked: false,
            projectName: c.project?.title || '',
          };
        })
      );

      setLatestChallenges(mappedChallenges);

      const { data: proofRows } = await supabase
        .from('ch_proofs')
        .select('id, number, rating, submitted_at, image_path, youtube_url, challenge:ch_challenges(id, title, project:ch_projects(title))')
        .eq('is_public', true)
        .order('submitted_at', { ascending: false })
        .limit(8);

      const mappedProofs = await Promise.all(
        (proofRows || []).map(async (p, idx) => {
          const thumb = p.youtube_url ? getYoutubeThumb(p.youtube_url) : '';
          const imgUrl = p.image_path
            ? await getCachedSignedUrl({ bucket: 'proof-images', path: p.image_path })
            : (thumb || '');

          return {
            id: p.id,
            image: imgUrl,
            imageAlt: p.challenge?.title ? `Proof image for ${p.challenge.title}` : 'Proof image',
            challengeId: p.challenge?.id || '',
            challengeName: p.challenge?.title || '챌린지',
            projectName: p.challenge?.project?.title || '',
            stars: Number(p.rating || 0),
            submittedAt: p.submitted_at,
            proofNumber: p.number ?? idx + 1,
            isPublic: true,
            youtubeUrl: p.youtube_url || '',
          };
        })
      );

      setRecentPublicProofs(mappedProofs);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const projectGrid = useMemo(() => publicProjects, [publicProjects]);
  const challengeGrid = useMemo(() => latestChallenges, [latestChallenges]);
  const proofGrid = useMemo(() => recentPublicProofs, [recentPublicProofs]);
  const feedGrid = useMemo(() => recentFeedPosts, [recentFeedPosts]);

  return {
    isLoading,
    projectGrid,
    challengeGrid,
    proofGrid,
    feedGrid,
    recentNotices,
    recentLabs,
    newUsers,
    monthlyRankings,
    reload: load,
  };
};

export default useLandingData;
